import { useMemo } from 'react';
import { findQuickLinkPage, isQuickLinkPageAvailable } from '@/config/quickLinkPages';
import { usePermissions } from '@/hooks/usePermissions';
import { useUiPreferences } from '@/hooks/useUiPreferences';
import { logger } from '@/lib/logger';
import { useFeatureFlagsContext } from '@/providers/FeatureFlagsProvider';
import { expandPath } from '@/utils/expandPath';

interface Result {
  effectiveHomePath: string | null;
  isLoading: boolean;
}

export function useEffectiveHomePath(activeClientId: string | undefined | null): Result {
  const { data, isLoading: isPrefsLoading } = useUiPreferences();
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions();
  const { flags } = useFeatureFlagsContext();

  return useMemo(() => {
    if (isPrefsLoading || isPermissionsLoading) {
      return { effectiveHomePath: null, isLoading: true };
    }

    const homepagePath = data.homepage_path;
    if (!homepagePath) return { effectiveHomePath: null, isLoading: false };

    const catalogEntry = findQuickLinkPage(homepagePath, hasPermission);
    if (!catalogEntry) return { effectiveHomePath: null, isLoading: false };

    if (
      !hasPermission(
        catalogEntry.requiredPermission.feature,
        catalogEntry.requiredPermission.action
      )
    ) {
      return { effectiveHomePath: null, isLoading: false };
    }

    if (!isQuickLinkPageAvailable(catalogEntry, flags)) {
      logger.debug(
        'useEffectiveHomePath: saved homepage is gated by a disabled feature flag, falling back to default',
        { path: homepagePath, flag: catalogEntry.featureFlag }
      );
      return { effectiveHomePath: null, isLoading: false };
    }

    const expanded = expandPath(homepagePath, { clientId: activeClientId ?? '' });
    return { effectiveHomePath: expanded, isLoading: false };
  }, [
    data.homepage_path,
    isPrefsLoading,
    isPermissionsLoading,
    hasPermission,
    activeClientId,
    flags,
  ]);
}
